import { authHeaders } from './account'
import { artifactThumbUrl, inputThumbUrl } from './jobClient'

/**
 * 我的历史：这个账号最近处理过的几趟。
 *
 * 背景：2026-09-11 用户裁决——「登录之后，要能够查看我的历史，不然找不到之前的东西」。
 *   作业一直挂在账号上（页脚写着「登录后产物一直保留」），可页面上从来没有一条回去的路：
 *   关掉标签页，那趟就只剩自己记着的作业号——而没人记得住。
 * 设计意图：两跳，与上传那条一模一样的形状。广州认人、签一张 120 秒的只读通行证，
 *   首尔拿通行证回一份列表——图和列表都不经过广州（见 gateway 的 history-pass）。
 * 关键约束：列表只给「认出是哪一趟」所需的那几样。读数、轨迹、产物清单都不在里面，
 *   点进去（?job=…）自然会取全量快照，不必为列表提前把二十份作业目录读一遍。
 */
export interface HistoryItem {
  id: string
  createdAt: number
  /** 这一趟的结局，已经翻成页面的说法（见 outcomeOf）。 */
  state: 'done' | 'stalled' | 'failed' | 'cancelled' | 'expired' | 'waiting' | 'running'
  /** 走过深度修复（画面被重画过）。 */
  deep: boolean
  /** 这一趟命中了缓存，没花算力也没收钱。 */
  cached: boolean
  pixels: [number, number]
  /** 封面图。优先成品，没有成品就用原图——失败那几趟只有它能让人认出是哪一次。 */
  coverUrl: string | null
}

export async function fetchHistory(signal?: AbortSignal): Promise<HistoryItem[]> {
  const gw = await fetch('/gateway/tools/ripple/history-pass',
                         { headers: authHeaders(), signal })
  if (!gw.ok) return []          // 没登录、被封、工具没配票据：历史整块不出现
  const { pass } = await gw.json() as { pass?: string }
  if (!pass) return []

  const base = `${import.meta.env.BASE_URL}api`
  const resp = await fetch(`${base}/jobs?pass=${encodeURIComponent(pass)}&limit=24`, { signal })
  if (!resp.ok) return []
  const d = await resp.json() as { items?: RawItem[] }
  return (d.items ?? []).map(toItem)
}

interface RawItem {
  id: string
  created_at: number
  phase: string
  outcome: string
  deep: boolean
  cached: boolean
  pixels: [number, number]
  cover: string
  has_files: boolean
}

function toItem(r: RawItem): HistoryItem {
  return {
    id: r.id,
    createdAt: r.created_at * 1000,
    state: stateOf(r),
    deep: r.deep,
    cached: r.cached,
    pixels: r.pixels,
    coverUrl: r.cover ? artifactThumbUrl(r.id, r.cover)
      : r.has_files ? inputThumbUrl(r.id)
      : null,
  }
}

/**
 * 把服务端的相位与结局并成一个词。
 *
 * 「跑完了但没有成品」（`delivered` 之外的那几种结局）不能和「处理完成」混在一起：
 * 用户回来找的是那张图，而那一趟根本没有图，说成「完成」等于让他白点一次。
 */
function stateOf(r: RawItem): HistoryItem['state'] {
  if (r.phase === 'cancelled') return 'cancelled'
  if (r.phase === 'failed') return 'failed'
  // 等超时与用户取消是两件事，列表上也不能混（2026-09-12 用户实撞：「这个没有取消啊」）。
  // 混着标的时候，一趟自己等到超时的作业在这里写着「已取消」，用户找不到自己什么时候
  // 取消过它，只能怀疑列表在乱说。
  if (r.phase === 'expired') return 'expired'
  if (r.phase === 'awaiting_confirm' || r.phase === 'awaiting_regen') return 'waiting'
  if (r.phase !== 'done') return 'running'
  return r.outcome.startsWith('delivered') ? 'done' : 'stalled'
}
